import { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { IntegrationsPage } from './components/IntegrationsPage';
import { PositionsPage } from './components/PositionsPage';
import { ChatPage } from './components/ChatPage';
import { ProfilePage } from './components/ProfilePage';
import { SubscriptionsPage } from './components/SubscriptionsPage';
import { BottomNavigation } from './components/BottomNavigation';

export type PageType = 'integrations' | 'positions' | 'chat' | 'profile' | 'subscriptions';

export interface ChatThread {
  id: string;
  title: string;
  integration: string;
  position: string;
  lastMessage: string;
  timestamp: Date;
  messageCount: number;
}

export default function App() {
  const [currentPage, setCurrentPage] = useState<PageType>('integrations');
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([
    {
      id: 'thread-1',
      title: 'Настройка CRM системы',
      integration: 'salesforce',
      position: 'sales-manager',
      lastMessage: 'Понял ваш вопрос. Для решения этой задачи...',
      timestamp: new Date(Date.now() - 3600000), // 1 час назад
      messageCount: 12
    },
    {
      id: 'thread-2',
      title: 'Обучение новых пользователей',
      integration: 'salesforce',
      position: 'sales-admin',
      lastMessage: 'Да, я могу помочь с этим...',
      timestamp: new Date(Date.now() - 86400000), // 1 день назад
      messageCount: 8
    }
  ]);

  const handleIntegrationSelect = (integration: string) => {
    setSelectedIntegration(integration);
    setCurrentPage('positions');
  };

  const handlePositionSelect = (position: string) => {
    setSelectedPosition(position);
    // Создаем новую ветку чата
    createNewChatThread(selectedIntegration!, position);
  };

  const createNewChatThread = (integration: string, position: string) => {
    const newThread: ChatThread = {
      id: `thread-${Date.now()}`,
      title: 'Новый чат',
      integration,
      position,
      lastMessage: 'Чат начат',
      timestamp: new Date(),
      messageCount: 1
    };
    
    setChatThreads(prev => [newThread, ...prev]);
    setCurrentThreadId(newThread.id);
    setCurrentPage('chat');
  };

  const handleThreadSelect = (threadId: string) => {
    const thread = chatThreads.find(t => t.id === threadId);
    if (thread) {
      setCurrentThreadId(threadId);
      setSelectedIntegration(thread.integration);
      setSelectedPosition(thread.position);
      setCurrentPage('chat');
    }
  };

  const updateChatThread = (threadId: string, updates: Partial<ChatThread>) => {
    setChatThreads(prev => prev.map(thread => 
      thread.id === threadId ? { ...thread, ...updates } : thread
    ));
  };

  const handleBackToIntegrations = () => {
    setCurrentPage('integrations');
    setSelectedIntegration(null);
    setSelectedPosition(null);
  };

  const handleBackToChats = () => {
    setCurrentPage('integrations'); // Возвращаемся к списку интеграций как главной странице
  };

  const showBottomNav = currentPage !== 'positions'; // Скрываем навигацию только на странице выбора позиций

  return (
    <Box minH="100vh" bg="gray.50" position="relative">
      <Box 
        pb={showBottomNav && currentPage !== 'chat' ? "80px" : "0"} // No padding for chat pages
        h={currentPage === 'chat' ? "calc(100vh - 80px)" : "auto"} // Chat takes full height minus navigation
        minH={currentPage !== 'chat' ? "100vh" : "auto"}
        overflowY={currentPage !== 'chat' ? "auto" : "hidden"} // Chat handles its own scrolling
        bg={currentPage === 'chat' ? "transparent" : "gray.50"} // Remove background for chat
      >
        {currentPage === 'integrations' && (
          <IntegrationsPage 
            onIntegrationSelect={handleIntegrationSelect}
            chatThreads={chatThreads}
            onThreadSelect={handleThreadSelect}
          />
        )}
        
        {currentPage === 'positions' && (
          <PositionsPage 
            integration={selectedIntegration!}
            onPositionSelect={handlePositionSelect}
            onBack={handleBackToIntegrations}
          />
        )}
        
        {currentPage === 'chat' && (
          <Box h="calc(100vh - 80px)" overflow="hidden">
            <ChatPage 
              integration={selectedIntegration!}
              position={selectedPosition!}
              threadId={currentThreadId!}
              onBack={handleBackToChats}
              onUpdateThread={updateChatThread}
              onNewChat={() => createNewChatThread(selectedIntegration!, selectedPosition!)}
            />
          </Box>
        )}

        {currentPage === 'profile' && (
          <ProfilePage />
        )}

        {currentPage === 'subscriptions' && (
          <SubscriptionsPage />
        )}
      </Box>

      {showBottomNav && (
        <BottomNavigation 
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}
    </Box>
  );
}